// src/components/admin/tabs/RuntimeSecretsTab.jsx
// 运行时密钥：二次口令解锁、掩码列表、在线覆盖与恢复环境配置。
// 安全交互（二次口令校验、X-Config-Passphrase 头、掩码展示、confirm 流程）与 AdminDashboard.jsx 原实现逐字一致，仅重构展示层。
import { appConfirm } from '../../../services/appDialogs';
import { AppSelect } from '../../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CheckCircle, Eye, EyeOff, KeyRound, Lock,
  RefreshCw, RotateCcw, Save, Search, Unlock,
} from "lucide-react";
import {
  fetchRuntimeSecrets, verifyRuntimeSecretPassphrase, updateRuntimeSecret, resetRuntimeSecret,
} from "../../../services/adminService";
import { formatTraceTime } from "../adminUtils";
import {
  ADMIN_ACCENT, EmptyBlock, ErrorNotice, Panel, PrimaryButton, SecondaryButton, StatusBadge,
} from "../adminUi.jsx";

const ACCENT = ADMIN_ACCENT;

const RuntimeSecretsTab = () => {
  const [items, setItems] = useState([]);
  const [writeEnabled, setWriteEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('全部');
  const [passphrase, setPassphrase] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [showDrafts, setShowDrafts] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchRuntimeSecrets();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setWriteEnabled(Boolean(data?.writeEnabled));
    } catch (e) {
      setError(e?.message || '密钥配置加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(() => (
    ['全部', ...Array.from(new Set(items.map(item => item.category).filter(Boolean)))]
  ), [items]);

  const filteredItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      if (category !== '全部' && item.category !== category) return false;
      if (!needle) return true;
      return [item.label, item.key, item.description]
        .some(value => String(value || '').toLowerCase().includes(needle));
    });
  }, [items, query, category]);

  const sourceLabel = (source) => ({
    runtime: '在线覆盖',
    environment: '环境配置',
    unconfigured: '未配置',
  }[source] || '未配置');

  const sourceColor = (source) => ({
    runtime: 'var(--color-success)',
    environment: 'var(--color-info)',
    unconfigured: 'var(--color-text-muted)',
  }[source] || 'var(--color-text-muted)');

  const replaceItem = useCallback((saved) => {
    if (!saved?.key) return;
    setItems(current => current.map(item => item.key === saved.key ? { ...item, ...saved } : item));
  }, []);

  const handleUnlock = async (event) => {
    event.preventDefault();
    if (!passphrase) {
      setError('请输入二次口令');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      await verifyRuntimeSecretPassphrase(passphrase);
      setUnlocked(true);
      setNotice('已解锁本次密钥编辑');
    } catch (e) {
      setUnlocked(false);
      setError(e?.message || '二次口令验证失败');
    } finally {
      setVerifying(false);
    }
  };

  const lockEditor = () => {
    setUnlocked(false);
    setPassphrase('');
    setDrafts({});
    setShowDrafts(false);
    setNotice('密钥编辑已锁定');
  };

  const saveSecret = async (item) => {
    const value = String(drafts[item.key] || '').trim();
    if (!value) {
      setError(`请输入“${item.label}”的新值`);
      return;
    }
    setBusyKey(item.key);
    setError('');
    setNotice('');
    try {
      const saved = await updateRuntimeSecret(item.key, value, passphrase);
      replaceItem(saved);
      setDrafts(current => ({ ...current, [item.key]: '' }));
      setNotice(`${item.label} 已更新，下一次请求立即生效`);
    } catch (e) {
      setError(e?.message || `${item.label} 更新失败`);
    } finally {
      setBusyKey('');
    }
  };

  const resetSecret = async (item) => {
    if (!item.override) return;
    if (!(await appConfirm(`确认移除“${item.label}”的在线覆盖，恢复服务器环境配置吗？`))) return;
    setBusyKey(item.key);
    setError('');
    setNotice('');
    try {
      const saved = await resetRuntimeSecret(item.key, passphrase);
      replaceItem(saved);
      setDrafts(current => ({ ...current, [item.key]: '' }));
      setNotice(`${item.label} 已恢复环境配置`);
    } catch (e) {
      setError(e?.message || `${item.label} 恢复失败`);
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="space-y-4">
      {/* 页头：标题 + 刷新/锁定 */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <KeyRound size={15} style={{ color: ACCENT }} />
            运行时密钥
          </h2>
          <p className="text-xs mt-1 max-w-3xl" style={{ color: 'var(--color-text-muted)' }}>
            在线替换模型、视频与云服务凭证。页面只显示掩码，新值加密保存，更新后无需重启服务。
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SecondaryButton onClick={load} disabled={loading} className="inline-flex items-center gap-1.5">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            刷新
          </SecondaryButton>
          {unlocked && (
            <SecondaryButton onClick={lockEditor} className="inline-flex items-center gap-1.5">
              <Lock size={13} />
              锁定
            </SecondaryButton>
          )}
        </div>
      </div>

      {!writeEnabled && !loading && (
        <div className="rounded-lg px-3.5 py-2.5 text-sm flex items-start gap-2"
          style={{ backgroundColor: 'color-mix(in srgb, var(--color-warning) 10%, transparent)', border: '1px solid var(--color-warning)', color: 'var(--color-warning)' }}>
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>在线修改尚未启用。请在服务器配置 <span className="font-mono-num">ADMIN_CONFIG_PASSPHRASE</span> 和 <span className="font-mono-num">ADMIN_CONFIG_ENCRYPTION_KEY</span> 后重启一次。</span>
        </div>
      )}

      {writeEnabled && !unlocked && (
        <div className="flex justify-center">
          <Panel title="解锁密钥编辑" sub="验证二次口令后才能修改在线覆盖" className="w-full max-w-md">
            <form onSubmit={handleUnlock} className="space-y-3">
              <div>
                <label className="text-label block mb-1.5">二次口令</label>
                <input type="password" value={passphrase} onChange={event => setPassphrase(event.target.value)}
                  autoComplete="new-password" placeholder="输入密钥管理口令"
                  className="w-full h-8 px-2.5 rounded-md text-xs outline-none"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} />
              </div>
              <button type="submit" disabled={verifying}
                className="w-full h-8 px-3.5 rounded-md text-xs font-semibold inline-flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50 transition-colors duration-150"
                style={{ backgroundColor: ACCENT, color: 'var(--color-bg-primary)' }}>
                <Unlock size={13} />
                {verifying ? '验证中...' : '解锁编辑'}
              </button>
              <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                口令仅保留在当前页面内存中，离开页面或锁定后立即清除。
              </p>
            </form>
          </Panel>
        </div>
      )}

      {error && <ErrorNotice message={error} />}
      {notice && (
        <div className="rounded-lg px-3.5 py-2.5 text-sm flex items-center gap-2 animate-in fade-in slide-in-from-top-1 duration-200"
          style={{ backgroundColor: 'color-mix(in srgb, var(--color-success) 10%, transparent)', border: '1px solid var(--color-success)', color: 'var(--color-success)' }}>
          <CheckCircle size={14} />{notice}
        </div>
      )}

      <Panel
        title="密钥列表"
        sub="掩码展示 · 解锁后可在线覆盖"
        right={(
          <div className="flex items-center gap-2">
            {unlocked && (
              <button type="button" onClick={() => setShowDrafts(value => !value)}
                className="w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer transition-colors duration-150"
                title={showDrafts ? '隐藏正在输入的内容' : '显示正在输入的内容'}
                style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-secondary)' }}>
                {showDrafts ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            )}
            <span className="text-label"><span className="font-mono-num">{filteredItems.length}</span> 项</span>
          </div>
        )}
      >
        <div className="flex flex-wrap gap-2.5 items-center mb-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
            <input value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索线路、Key 或用途"
              className="w-full h-8 pl-8 pr-2.5 rounded-md outline-none text-xs"
              style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} />
          </div>
          <AppSelect value={category} onChange={event => setCategory(event.target.value)}
            className="h-8 px-2 rounded-md text-xs outline-none"
            style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}>
            {categories.map(value => <option key={value} value={value}>{value}</option>)}
          </AppSelect>
        </div>

        {loading ? (
          <div className="space-y-2.5">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="rounded-lg border p-3.5 space-y-2" style={{ borderColor: 'var(--color-border-secondary)' }}>
                <div className="skeleton h-3 w-1/3" />
                <div className="skeleton h-3 w-2/3" />
              </div>
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <EmptyBlock text="没有符合条件的配置" />
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--color-border-secondary)' }}>
            {filteredItems.map(item => {
              const busy = busyKey === item.key;
              return (
                <div key={item.key} className="py-3.5 first:pt-0 last:pb-0 grid gap-4 xl:grid-cols-[minmax(260px,1.2fr)_minmax(170px,.65fr)_minmax(260px,1fr)_auto] xl:items-center"
                  style={{ borderColor: 'var(--color-border-secondary)' }}>
                  <div className="min-w-0">
                    <div className="font-medium text-[13px] truncate" style={{ color: 'var(--color-text-primary)' }} title={item.label}>{item.label}</div>
                    <div className="text-label mt-1 break-all">{item.key}</div>
                    <div className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{item.description}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge label={sourceLabel(item.source)} color={sourceColor(item.source)} />
                      <span className="font-mono-num text-xs" style={{ color: 'var(--color-text-secondary)' }}>{item.masked || '—'}</span>
                    </div>
                    {item.updatedAt && (
                      <div className="text-2xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
                        {item.updatedBy || '管理员'} · <span className="font-mono-num">{formatTraceTime(item.updatedAt)}</span>
                      </div>
                    )}
                  </div>
                  <input type={showDrafts ? 'text' : 'password'} value={drafts[item.key] || ''}
                    disabled={!unlocked || busy} autoComplete="new-password" spellCheck={false}
                    onChange={event => setDrafts(current => ({ ...current, [item.key]: event.target.value }))}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        saveSecret(item);
                      }
                    }}
                    placeholder={unlocked ? '输入新值，留空不会修改' : '解锁后可修改'}
                    className="w-full h-8 px-2.5 rounded-md outline-none text-xs disabled:opacity-50"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} />
                  <div className="flex items-center gap-2 xl:justify-end">
                    <PrimaryButton onClick={() => saveSecret(item)}
                      disabled={!unlocked || busy || !String(drafts[item.key] || '').trim()}
                      className="inline-flex items-center gap-1.5">
                      <Save size={13} />{busy ? '处理中' : '保存'}
                    </PrimaryButton>
                    <button type="button" onClick={() => resetSecret(item)} title="恢复环境配置"
                      disabled={!unlocked || busy || !item.override}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-md border cursor-pointer disabled:opacity-40 transition-colors duration-150"
                      style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-tertiary)' }}>
                      <RotateCcw size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default RuntimeSecretsTab;
